import {
  rng,
  request,
  isEnglishCheck,
  hideAndDisable,
  unhideAndEnable,
  isMobile,
  isTablet,
  inRange,
} from "./util";



const fetchedBlogPosts = [];

export const initBlogArchive = async() => {

  // preload blogposts to be re-used by each blog slider
  // prevents refetching on resize
  if (fetchedBlogPosts.length === 0) {
    const resp = await request(`${isEnglishCheck() ? "/en" : ""}/blog-search.json`);
    fetchedBlogPosts.push(...resp);
  }

  // find all blogslider roots and configure them
  Array.from(document.getElementsByClassName("slider-archive-root"))
    .forEach((element) => configureBlogArchive(element));

};

/**
 * @param {HTMLElement} root
 */
const configureBlogArchive = async(root) => {
  let isRegistered = false;
  if (isRegistered) return;
  let leftArrow = root.querySelectorAll(".blog-archive-button-left")[0] ?? null;
  let rightArrow = root.querySelectorAll(".blog-archive-button-right")[0] ?? null;
  let sliderRoot = root.querySelectorAll(".blog-archive-root")[0] ?? null;

  if (!leftArrow || !rightArrow || !sliderRoot) {
    return;
  }

  if (sliderRoot.innerHTML.trim() !== "") {
    return;
  }

  // determine which grid format to use
  // 4x2, 2x1 or 1x1
  let indexOffset = isTablet() ? window.innerHeight >= 1366 ? 4 : 2 : isMobile() ? 1 : 8;


  const tagFilterAttr = sliderRoot.getAttribute("tags") ?? "";
  const filterTags = tagFilterAttr.split(",").map((s) => s.trim()).filter((s) => s !== "");


  let docs = [...fetchedBlogPosts];

  if (filterTags.length > 0) {
    const ret = [];
    for (const tag of filterTags) {
      const res = docs.filter((d) => d?.tags?.includes(tag));
      ret.push(...res);
    }
    const unique = [];
    for (const val of ret) {
      if (!unique.find((d) => d.title === val.title)) {
        unique.push(val);
      }
    }
    docs = unique;
  }

  docs.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // detect shown blogposts and use it as an offset
  // to not show the same blog posts
  // this is only relevant on /blogs
  const shownBlogpostCount = document.querySelectorAll(".articles > .summary").length;


  // slice up the blogposts into slides based of the offset
  // i.e. posts per slide
  // e.g. 4x2 layout => 8 posts per slide
  let blogSlides = [];
  for (let i = shownBlogpostCount; i < docs.length; i += indexOffset) {
    blogSlides.push(docs.slice(i, i + indexOffset));
  }

  let slideGrid = createBlogSlidesElements(blogSlides);

  sliderRoot.appendChild(slideGrid);

  let archiveWidth = slideGrid.scrollWidth / blogSlides.length;

  slideGrid.scrollLeft = 0;

  let index = 0;
  const renderButtons = () => {
    showLeftButton(index > 0);
    showRightButton(index < blogSlides.length - 1);
  };

  const showLeftButton = (show) => {
    if (!show) {
      hideAndDisable(leftArrow);
    } else {
      unhideAndEnable(leftArrow);
    }
  };

  const showRightButton = (show) => {
    if (show) {
      unhideAndEnable(rightArrow);
    } else {
      hideAndDisable(rightArrow);
    }
  };

  renderButtons();

  if (isMobile() || isTablet()) {
    slideGrid.addEventListener("scroll", (e) => {
      index = Math.round(e.target.scrollLeft / archiveWidth);
      renderButtons();
    });
  }


  const onLeftClick = () => {
    index = index === 0 ? 0 : index - 1;
    slideGrid.scrollLeft = index * archiveWidth;
    renderButtons();
  };

  const onRightClick = () => {
    index = index === blogSlides.length - 1 ? index : index + 1;
    slideGrid.scrollLeft = index * archiveWidth;
    renderButtons();
  };

  leftArrow.addEventListener("click", onLeftClick);
  rightArrow.addEventListener("click", onRightClick);

  slideGrid.scrollLeft = 0;
  slideGrid.scrollLeft = index * slideGrid.clientWidth;

  isRegistered = true;

  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  const onResize = async() => {
    if (!isRegistered) return;
    archiveWidth = slideGrid.scrollWidth / blogSlides.length;
    slideGrid.scrollLeft = index * archiveWidth;

    const newOffset = isTablet() ? window.innerHeight >= 1366 ? 4 : 2 : isMobile() ? 1 : 8;
    if (newOffset === indexOffset) {
      return;
    }

    const verticalGrow = Math.abs(windowHeight - window.innerHeight);
    const horizontalGrow = Math.abs(windowWidth - window.innerWidth);
    // nothing to do
    if (verticalGrow === 0 && horizontalGrow === 0) {
      return;
    }

    // When scrolling on mobile, the address bar will pop in or out causing resize events.
    // See here: https://developer.chrome.com/blog/url-bar-resizing/
    // Probably can't apply that without breaking something else.
    // On a Samsung Galaxy A52s its 57px, on a Huawei P Smart 2019 its 56px ¯\_(ツ)_/¯ (both Chrome)
    // 56px seems to also fix it on iPhones, although I can't verify that and don't know
    // which specific iPhone.
    // 15/10/2022
    // On Iphones there's both a vertical and horizontal growth due it having
    // a bottom and top bar in some browsers. The resize events also seem to
    // fire differently and the resize growth is very inconsistent.
    // Technically, we would only have to resize on a horizontal growth but that would
    // require further testing.
    if (isMobile() && inRange(verticalGrow, 0, 200) && inRange(horizontalGrow, 0, 200)) {
      return;
    }

    if (slideGrid) {
      slideGrid.innerHTML = "";
      sliderRoot.removeChild(slideGrid);
    }
    sliderRoot.innerHTML = "";
    leftArrow.removeEventListener("click", onLeftClick);
    rightArrow.removeEventListener("click", onRightClick);
    indexOffset = -1;
    docs = [];
    index = 0;
    sliderRoot = null;
    leftArrow = null;
    rightArrow = null;
    slideGrid = null;
    blogSlides = [];
    archiveWidth = -1;
    isRegistered = false;
    await configureBlogArchive(root);
  };


  window.addEventListener("resize", () => {
    let debounce = null;
    if (debounce) {
      clearTimeout(debounce);
    }
    debounce = setTimeout(onResize, 100);
  });

};


const createBlogSlidesElements = (slides) => {
  const root = document.createElement("div");
  root.className = "slide-grid";
  slides.forEach((blogSlide) => {
    const slideContainer = document.createElement("div");
    slideContainer.classList.add("slide-container");
    const slide = document.createElement("div");
    slide.classList.add("slide");
    blogSlide.forEach((blog) => {
      slide.appendChild(createArchiveEntry(blog));
    });
    slideContainer.appendChild(slide);
    root.appendChild(slideContainer);
  });
  return root;
};


const placeholderBasePath = "/alpha/";
const placeholderImages = [
  "burg_sm.png",
  "schachbrett_sm.png",
  "stacheldraht_sm.png",
  "eule_final_sm.png",
  "igel_sm.png",
  "maschine_sm.png"
];

const createArchiveEntry = (blog) => {
  let thumb = "";
  const imageClasses = ["imagecontainer"];
  if (blog.sliderThumbnails && blog.sliderThumbnails.length > 0) {
    // Use pre-processed slider thumbnail
    const sliderThumb = blog.sliderThumbnails[0];
    thumb = sliderThumb.src;
  } else if (blog.resources) {
    // Fall back to original resources if slider thumbnails aren't available
    const thumbRes = blog.resources.find((b) => b.name === "thumbnail");
    const titleRes = blog.resources.find((b) => b.name === "title");
    if (thumbRes || titleRes) {
      thumb = `${blog.ref}${thumbRes ? thumbRes.src : titleRes.src}`;
    }
  } else {
    // Use placeholder if no images available
    imageClasses.push("placeholder");
    thumb = `${placeholderBasePath}${placeholderImages[rng(placeholderImages.length)]}`;
  }
  let dateStr = "";
  if (blog.date) {
    const date = new Date(Date.parse(blog.date));
    dateStr = `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
  }

  const tagElements = blog.tags
    ? blog.tags.map(
      (t) => `<a href="${isEnglishCheck() ? "/en" : ""}/blog/tags/${t.replaceAll(" ", "-")}" class="label-background"><span class="label-label">#${t}</a>`
    )
    : [];
  if (tagElements.length > 0) {
    dateStr += " – ";
  }
  const root = document.createElement("article");
  root.classList.add("summary-archive");
  root.classList.add(isTablet() ? "twoBytwo" : "fourByfour");
  root.id = blog.ref;
  root.innerHTML = `
  <a href="${blog.ref}">
    <figure id="${blog.ref}-thumb" class="${imageClasses.join(" ")}"></figure>
  </a>
  <div class="description">
    <a class="title-link" href="${blog.ref}">
        <h2 class="summaryTitle">${blog.title}</h2>
    </a>
    <p class="tags-and-date">
      ${dateStr}
      ${tagElements.length > 0 ? `${tagElements.slice(0, 2).join("")}` : ""}
    </p>
  </div>
  `;
  const figure = root.querySelector("a>figure");
  figure.appendChild(preloadImage(thumb, blog.title));
  return root;
};


const preloadImage = (url, title) => {
  const img = new Image();
  img.src = url;
  img.alt = `${title} thumbnail`;
  img.onerror = `this.onerror=null;this.src="${placeholderBasePath}${placeholderImages[rng(placeholderImages.length)]}";`;
  return img;
};
